import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { PaginationSearchParams, UseQueryConfig } from '@/api/types';
import { PaginatedDriverListFilters } from '@/api/apis/resources';
import { queryClient } from '..';
import { getBannedDriverIdWithProvider, getBannedDriversListWithProvider } from './utils';
import { bannedDriversQueryKeys } from './banned-drivers.keys';

const allBannedTailNumbersPaginationParams: PaginationSearchParams = {
  itemsPerPage: 100,
  pageIndex: 0,
};

export const bannedDriversQuery = {
  all: () =>
    queryOptions({
      queryKey: bannedDriversQueryKeys.getList(allBannedTailNumbersPaginationParams),
      queryFn: () => api.resource.getBannedDriversList(allBannedTailNumbersPaginationParams),
    }),
  list: (listQuery: PaginatedDriverListFilters) =>
    queryOptions({
      queryKey: bannedDriversQueryKeys.getList(listQuery),
      queryFn: () => getBannedDriversListWithProvider(listQuery),
      placeholderData: keepPreviousData,
    }),
  item: (bannedDriverId: string) =>
    queryOptions({
      queryKey: bannedDriversQueryKeys.getItem(bannedDriverId),
      queryFn: () => getBannedDriverIdWithProvider(bannedDriverId),
    }),
};

const useBannedDriversAllQuery = () => {
  return useQuery(bannedDriversQuery.all());
};

const useBannedDriversListQuery = (listQuery: PaginationSearchParams, options?: UseQueryConfig) => {
  return useQuery({
    ...bannedDriversQuery.list(listQuery),
    ...options,
  });
};

const useBannedDriversByIdQuery = (id: string) => {
  return useQuery({
    ...bannedDriversQuery.item(id),
    staleTime: 60 * 1000,
    initialData: () => {
      const query = queryClient.getQueryData(bannedDriversQuery.all().queryKey);
      return query?.items.find((c: { id: string }) => c.id === id);
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(bannedDriversQuery.all().queryKey)?.dataUpdatedAt,
    placeholderData: keepPreviousData,
  });
};

export { useBannedDriversAllQuery, useBannedDriversListQuery, useBannedDriversByIdQuery };
